import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Handshake, Info, Pencil, Trash2, CheckCircle2, Clock, FileText, Loader2, Camera, X } from "lucide-react";
import { api, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FdpPromoCard } from "@/components/FdpPromoCard";

const src = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
const EMPTY = { description: "", quantity: 1, priority: "normal", school_photo_path: "" };
const STATUS = {
  pendente: { l: "Pendente", c: "bg-orange-100 text-orange-700", i: Clock },
  feito: { l: "Feito", c: "bg-emerald-100 text-emerald-700", i: CheckCircle2 },
  convertido: { l: "Em orçamento", c: "bg-violet-100 text-violet-700", i: FileText },
};

const ItemForm = ({ form, setForm, onSubmit, saving, editing, onCancel }) => {
  const [up, setUp] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, school_photo_path: r.data.path }));
    } catch { toast.error("Erro ao enviar foto"); } finally { setUp(false); }
  };
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-slate-100 card-shadow p-5 space-y-4" data-testid="s-fdp-form">
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1.5">O que precisa ser feito? <span className="text-red-500">*</span></label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={800}
          placeholder="Ex.: Trocar 3 lâmpadas da sala 5, consertar torneira do banheiro..." data-testid="s-fdp-desc"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]" />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Quantidade</label>
          <input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 1 })}
            className="w-24 h-10 px-3 rounded-xl border border-slate-200 text-sm" data-testid="s-fdp-qty" />
        </div>
        <div className="flex gap-2">
          {[["normal", "Normal", "bg-blue-100 text-blue-700 border-blue-200"], ["urgente", "Urgente", "bg-red-100 text-red-700 border-red-200"]].map(([v, l, c]) => (
            <button key={v} type="button" onClick={() => setForm({ ...form, priority: v })} data-testid={`s-fdp-priority-${v}`}
              className={`px-3 h-10 rounded-lg border text-sm font-semibold ${form.priority === v ? c : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <label className="relative h-10 px-3 rounded-lg border border-dashed border-slate-300 flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer" data-testid="s-fdp-photo">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          {up ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {form.school_photo_path ? "Foto anexada" : "Foto (opcional)"}
        </label>
        {form.school_photo_path && <button type="button" onClick={() => setForm({ ...form, school_photo_path: "" })} className="text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>}
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        {editing && <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>}
        <Button type="submit" disabled={saving || up} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="s-fdp-submit">
          <Send className="w-4 h-4 mr-2" />{saving ? "Enviando..." : editing ? "Salvar item" : "Adicionar à lista"}
        </Button>
      </div>
    </form>
  );
};

const ItemCard = ({ it, onEdit, onDelete }) => {
  const st = STATUS[it.status] || STATUS.pendente;
  return (
    <li className="bg-white rounded-xl border border-slate-100 p-3 flex items-start gap-3" data-testid={`s-fdp-item-${it.id}`}>
      {it.school_photo_path && <img src={src(it.school_photo_path)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{it.quantity > 1 && <strong>{it.quantity}× </strong>}{it.description}</p>
        <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold ${st.c}`}><st.i className="w-3 h-3" />{st.l}{it.budget_number ? ` Nº ${it.budget_number}` : ""}</span>
          {formatDateBR(it.created_at?.slice(0, 10))}{it.priority === "urgente" && " · urgente"}
        </p>
      </div>
      {it.status === "pendente" && (
        <div className="flex gap-1">
          <button type="button" onClick={() => onEdit(it)} className="p-1.5 text-slate-400 hover:text-violet-600" data-testid={`s-fdp-edit-${it.id}`} aria-label="Editar"><Pencil className="w-4 h-4" /></button>
          <button type="button" onClick={() => onDelete(it)} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`s-fdp-del-${it.id}`} aria-label="Excluir"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
    </li>
  );
};

export default function SchoolNewRequest() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/school/fdp-list").then((r) => setData(r.data)).catch(() => setData({ fdp_active: false, items: [], requests: [] }));
  const [fdp, setFdp] = useState(null);
  useEffect(() => { load(); api.get("/school/fdp").then((r) => setFdp(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error("Descreva o serviço");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/school/fdp-list/${editing}`, form);
        toast.success("Item atualizado");
      } else {
        const r = await api.post("/school/fdp-list", form);
        toast.success(r.data.mode === "fdp" ? "Item adicionado à sua lista" : "Solicitação enviada! A AMC entrará em contato.");
      }
      setForm(EMPTY); setEditing(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar");
    } finally { setSaving(false); }
  };
  const remove = async (it) => {
    if (!window.confirm("Excluir este item da lista?")) return;
    try { await api.delete(`/school/fdp-list/${it.id}`); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao excluir"); }
  };
  const edit = (it) => { setEditing(it.id); setForm({ description: it.description, quantity: it.quantity || 1, priority: it.priority || "normal", school_photo_path: it.school_photo_path || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const list = data.fdp_active ? data.items : (data.requests || []).map((r) => ({ ...r, description: r.description || r.title }));
  return (
    <div className="max-w-2xl space-y-5" data-testid="s-fdp-page">
      <div>
        <h1 className="font-heading text-2xl font-black text-[#1e3a8a]">Solicitar serviços</h1>
        <p className="text-sm text-slate-500">Monte sua lista de serviços. Você pode voltar e adicionar mais itens quando quiser.</p>
      </div>
      {data.fdp_active ? (
        <div className="flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-xl p-3 text-sm text-violet-900" data-testid="s-fdp-active-banner">
          <Handshake className="w-5 h-5 shrink-0" /><span>Sua escola faz parte do <b>Faz Depois Paga</b>. Cada item enviado entra na programação da AMC e você acompanha aqui quando ficar pronto.</span>
        </div>
      ) : (
        <>
          <FdpPromoCard fdp={fdp} inRequest onSent={(at) => setFdp({ ...fdp, interest_sent_at: at })} />
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900" data-testid="s-fdp-inactive-banner">
            <Info className="w-5 h-5 shrink-0" /><span>Sua solicitação será enviada à AMC e entraremos em contato.</span>
          </div>
        </>
      )}
      <ItemForm form={form} setForm={setForm} onSubmit={submit} saving={saving} editing={!!editing} onCancel={() => { setEditing(null); setForm(EMPTY); }} />
      <section className="space-y-2">
        <h2 className="text-base font-bold text-slate-800">{data.fdp_active ? `Minha lista (${list.length})` : `Solicitações enviadas (${list.length})`}</h2>
        {list.length === 0 ? <p className="text-sm text-slate-400 italic">Nenhum item ainda.</p> : (
          <ul className="space-y-2">{list.map((it) => <ItemCard key={it.id} it={data.fdp_active ? it : { ...it, status: it.status === "pendente" ? "pendente_req" : it.status }} onEdit={edit} onDelete={remove} />)}</ul>
        )}
      </section>
    </div>
  );
}
